/**
 * *Add content* (`specs/M4-V8-forms.md` §2.2, acceptance B4).
 *
 * monday's catalogue, in monday's groups, measured from the sandbox
 * `[cap:sandbox-add-content.json]`. A kind whose **column type we have not built** is listed
 * and disabled with the module that will bring it, so the dialog's shape never changes when one
 * lands — the same rule the add-view gallery follows.
 */
import { FORM_QUESTION_KINDS, type FormQuestionKind } from '@elizmonday/shared';
import { useState } from 'react';

export interface ContentEntry {
  kind: FormQuestionKind;
  label: string;
  /** `null` when it is buildable today; otherwise the module that will bring it. */
  module: string | null;
}

/** monday's groups and their order, verbatim from the capture. */
export const ADD_CONTENT_GROUPS: { title: string; entries: ContentEntry[] }[] = [
  {
    title: 'Data input',
    entries: [
      { kind: 'short_text', label: 'Short text', module: null },
      { kind: 'long_text', label: 'Long text', module: null },
      { kind: 'number', label: 'Number', module: null },
      { kind: 'email', label: 'Email', module: 'M2 tier 2' },
      { kind: 'phone', label: 'Phone', module: 'M2 tier 2' },
      { kind: 'location', label: 'Location', module: 'M2 tier 3' },
      { kind: 'country', label: 'Country', module: 'M2 tier 3' },
    ],
  },
  {
    title: 'Selection',
    entries: [
      { kind: 'single_select', label: 'Single select', module: null },
      { kind: 'multi_select', label: 'Multi select', module: null },
    ],
  },
  {
    title: 'File',
    entries: [
      { kind: 'file', label: 'File', module: 'M4.V8b' },
      { kind: 'signature', label: 'Signature', module: 'M4.V8b' },
    ],
  },
  {
    title: 'Other',
    entries: [
      { kind: 'date', label: 'Date', module: null },
      { kind: 'date_range', label: 'Date range', module: 'M2 tier 2' },
      { kind: 'hour', label: 'Time', module: 'M2 tier 3' },
      { kind: 'boolean', label: 'True / False', module: null },
      { kind: 'rating', label: 'Rating', module: 'M2 tier 2' },
      { kind: 'link', label: 'Link', module: 'M2 tier 2' },
    ],
  },
  {
    title: 'monday board questions',
    entries: [
      { kind: 'name', label: 'Name', module: null },
      { kind: 'updates', label: 'Update', module: 'M4.V8b' },
      { kind: 'people', label: 'People', module: null },
      { kind: 'connected_boards', label: 'Connect boards', module: 'M2 tier 3' },
      { kind: 'subitems', label: 'Subitems', module: 'M3.S1' },
    ],
  },
  {
    title: 'Content blocks',
    entries: [
      { kind: 'display_text', label: 'Text', module: null },
      { kind: 'page_block', label: 'Page', module: 'M4.V8b' },
    ],
  },
];

/** Every kind is listed exactly once — the parity test asserts it against the SDL enum. */
export const ADD_CONTENT_KINDS = ADD_CONTENT_GROUPS.flatMap((group) =>
  group.entries.map((entry) => entry.kind),
);

export function AddContentDialog({
  hasNameQuestion,
  onAdd,
  onClose,
}: {
  hasNameQuestion: boolean;
  onAdd(kind: FormQuestionKind, label: string): void;
  onClose(): void;
}) {
  const [search, setSearch] = useState('');
  const term = search.trim().toLowerCase();

  return (
    <div className="ez-view-modal__scrim" data-testid="add-content-scrim" onClick={onClose}>
      <div
        className="ez-add-content"
        role="dialog"
        aria-modal="true"
        aria-label="Add content"
        data-testid="add-content-dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="ez-add-content__head">
          <h2>Add content</h2>
          <button type="button" aria-label="Close" data-testid="form-content-close" onClick={onClose}>×</button>
        </header>
        <input
          className="ez-add-content__search"
          placeholder="Search"
          aria-label="Search question types"
          value={search}
          data-testid="add-content-search"
          onChange={(event) => setSearch(event.target.value)}
        />
        <div className="ez-add-content__body">
          {ADD_CONTENT_GROUPS.map((group) => {
            const entries = group.entries.filter((entry) => entry.label.toLowerCase().includes(term));
            if (entries.length === 0) return null;
            return (
              <section key={group.title}>
                <h3>{group.title}</h3>
                <ul>
                  {entries.map((entry) => {
                    // monday's own tooltip: "A form can have only one name question".
                    const oneName = entry.kind === 'name' && hasNameQuestion;
                    const disabled = entry.module !== null || oneName;
                    return (
                      <li key={entry.kind}>
                        <button
                          type="button"
                          disabled={disabled}
                          data-testid={`add-content-${entry.kind}`}
                          title={
                            oneName
                              ? 'A form can have only one name question'
                              : entry.module === null
                                ? undefined
                                : `Coming with ${entry.module}`
                          }
                          onClick={() => onAdd(entry.kind, entry.label)}
                        >
                          {entry.label}
                          {entry.module !== null ? <span>{entry.module}</span> : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Every SDL kind is offered: the dialog is the catalogue, not a subset. */
export const MISSING_KINDS = FORM_QUESTION_KINDS.filter((kind) => !ADD_CONTENT_KINDS.includes(kind));
